import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { useSyncedUser } from '../../context/SyncedUserContext';
import Loader from '../ui/loader';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { 
  CreditCard, MapPin, Settings, LogOut, 
  BadgeCheck, Building, ChevronRight,
  Search, ChevronLeft, ChevronRight as ChevronRightIcon
} from 'lucide-react';
//logout
import {
  useLogout,
} from "blitzware-react-sdk";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { useNavigate } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const Profile = () => {
  const { t } = useTranslation();
  const { syncedUserData } = useSyncedUser();
  const user = syncedUserData?.user;
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    points: 0,
    recommendationsCount: 0,
    likesCount: 0,
    activeSpots: 0,
    totalEarnings: 0,
    transactions: []
  });
  const [, setPointTransfers] = useState([]);
  
  // Mobile view tracking
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  
  // Combined activities state
  const [combinedActivities, setCombinedActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  
  const logout = useLogout();

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  // Helper function to format transaction descriptions nicely
  const formatTransactionDescription = useCallback((type) => {
    const actionMap = {
      'recommendation_add': t('profile.activity.addedRecommendation'),
      'recommendation_delete': t('profile.activity.deletedRecommendation'),
      'recommendation_like': t('profile.activity.likedRecommendation'),
      'parking_share': t('profile.activity.sharedParking'),
      'parking_book': t('profile.activity.bookedParking'),
      'parking_cancel': t('profile.activity.canceledParking'),
      'referral_bonus': t('profile.activity.referralBonus'),
      'welcome_bonus': t('profile.activity.welcomeBonus'),
      'daily_parking_reward': t('profile.activity.dailyParkingReward')
    };
    
    return actionMap[type] || type.split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }, [t]);

  // Check for mobile view
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobileView(mobile);
      setItemsPerPage(mobile ? 4 : 6);
    };
    
    handleResize(); // Set initial values
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch data function with useCallback to prevent unnecessary re-renders
  const fetchData = useCallback(async () => {
    if (!user?.id) {
      console.log('Profile: No user ID available, skipping data fetch');
      return;
    }
    
    console.log('Profile: Fetching data for user ID:', user.id);
    
    try {
      setLoading(true);
      
      // Create API instances with auth headers
      const token = localStorage.getItem("access_token");
      const userApiInstance = axios.create({
        baseURL: process.env.REACT_APP_BACKEND_URL + "api/users",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });
      
      const parkingApiInstance = axios.create({
        baseURL: process.env.REACT_APP_BACKEND_URL + "api/parking",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });
      
      const [statsData, transfers] = await Promise.all([
        // Get user stats
        Promise.all([
          userApiInstance.post("/me/points", { userId: user.id }),
          userApiInstance.post("/me/points/transactions", { userId: user.id }),
          userApiInstance.post("/me/recommendations", { userId: user.id }),
          userApiInstance.post("/me/active-spots", { userId: user.id }),
          userApiInstance.post("/me/total-earnings", { userId: user.id })
        ]).then(([points, transactions, recommendations, activeSpots, totalEarnings]) => {
          console.log('Profile: API responses received:', { points: points.data, transactions: transactions.data, recommendations: recommendations.data, activeSpots: activeSpots.data, totalEarnings: totalEarnings.data });
          return {
            points: points.data.points,
            transactions: transactions.data.transactions,
            recommendationsCount: recommendations.data.count,
            likesCount: recommendations.data.likesCount,
            activeSpots: activeSpots.data.count,
            totalEarnings: totalEarnings.data.earnings,
          };
        }),
        // Get point transfers
        parkingApiInstance.post("/point-transfers", { userId: user.id }).then(response => {
          console.log('Profile: Point transfers response:', response.data);
          return response.data;
        })
      ]);
      setStats(statsData);
      console.log('User stats:', statsData);
      setPointTransfers(transfers);
      
      const transactions = statsData.transactions.map(transaction => ({
        ...transaction,
        source: 'activity',
        date: new Date(transaction.created_at),
        amountText: `${transaction.amount > 0 ? '+' : ''}${transaction.amount} pts`,
        description: formatTransactionDescription(transaction.type)
      }));
      
      const transfersFormatted = transfers.map(transfer => ({
        id: transfer.id,
        source: 'parking',
        type: 'parking_income',
        amount: transfer.points,
        amountText: `+${transfer.points} pts`,
        description: t('profile.activity.parkingIncome'),
        status: transfer.status,
        date: new Date(transfer.created_at),
        created_at: transfer.created_at
      }));
      
      // Create welcome bonus entry
      const welcomeBonus = {
        id: 'welcome-bonus',
        source: 'activity',
        type: 'welcome_bonus',
        amount: 500,
        amountText: '+500 pts',
        description: t('profile.activity.welcomeBonus'),
        date: new Date(user.created_at),
        created_at: user.created_at
      };

      // Combine all activities and always add welcome bonus at the beginning
      const combined = [welcomeBonus, ...transactions, ...transfersFormatted];
      setCombinedActivities(combined);
      setFilteredActivities(combined);
    } catch (error) {
      console.error('Error fetching profile data:', error);
      // Set default values to prevent UI from breaking
      setStats({
        points: 0,
        recommendationsCount: 0,
        likesCount: 0,
        activeSpots: 0,
        totalEarnings: 0,
        transactions: []
      });
      setPointTransfers([]);
      setCombinedActivities([]);
      setFilteredActivities([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.created_at, formatTransactionDescription, t]);

  // Fetch data when user changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const applyAllFilters = useCallback(() => {
    let filtered = combinedActivities.filter(item => {
      // Apply type filter
      const typeMatches = 
        typeFilter === 'all' || 
        (typeFilter === 'activity' && item.source === 'activity') ||
        (typeFilter === 'parking' && item.source === 'parking') ||
        item.type === typeFilter;
      
      // Apply search filter if query exists
      const searchMatches = !searchQuery || 
        item.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.type?.toLowerCase().includes(searchQuery.toLowerCase());
      
      // All conditions must be true
      return typeMatches && searchMatches;
    });
    
    // Default sort by date (newest first)
    filtered.sort((a, b) => b.date - a.date);
    
    setFilteredActivities(filtered);
  }, [combinedActivities, typeFilter, searchQuery]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
    applyAllFilters();
  }, [applyAllFilters]);
  
  // Update type filter
  const handleTypeFilterChange = (value) => {
    setTypeFilter(value);
  };

  // Get current items for pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredActivities.slice(indexOfFirstItem, indexOfLastItem);
  
  // Calculate total pages
  const totalPages = Math.ceil(filteredActivities.length / itemsPerPage);
  
  // Change page
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  // Go to specific page
  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  // Generate page numbers - limit visible pages on mobile
  const getPageNumbers = () => {
    if (isMobileView) {
      // On mobile, show fewer page numbers
      if (totalPages <= 3) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      } else {
        // Show current page and one on each side when possible
        if (currentPage === 1) {
          return [1, 2, 3];
        } else if (currentPage === totalPages) {
          return [totalPages - 2, totalPages - 1, totalPages];
        } else {
          return [currentPage - 1, currentPage, currentPage + 1];
        }
      }
    } else {
      // On desktop, show all pages
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
  };
  
  const pageNumbers = getPageNumbers();

  if (loading) return <Loader />;
  
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">User data not available</p>
        </div>
      </div>
    );
  }

  // Get user initials for avatar fallback
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase();
  };

  // Mobile activity card component for phone display
  const ActivityCard = ({ item }) => (
    <Card className="mb-3 overflow-hidden">
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium 
            ${item.source === 'activity' 
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' 
              : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
            }`}>
            {item.source === 'activity' ? t('profile.activity.label') : t('profile.parking.label')}
          </span>
          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium
            ${item.amount > 0 
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
              : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
            }`}>
            {item.amountText}
          </span>
        </div>
        <p className="font-medium text-sm mb-1">{item.description}</p>
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          {item.date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </p>
      </div>
    </Card>
  );

  // Profile Actions Card component - reused in both mobile and desktop
  const ProfileActionsCard = () => (
    <Card className="w-full p-4 md:p-6">
      <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4">{t('profile.actions.title')}</h3>
      <div className="space-y-3 md:space-y-4">
        <button 
          onClick={() => navigate('/settings')}
          className="flex items-center justify-between w-full p-3 md:p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700/50 transition-colors"
        >
          <div className="flex items-center gap-2 md:gap-3">
            <Settings className="w-4 h-4 md:w-5 md:h-5 text-neutral-600 dark:text-neutral-400" />
            <span className="text-sm md:text-base">{t('profile.actions.settings')}</span>
          </div>
          <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-neutral-400" />
        </button>
        <button 
          onClick={logout}
          className="flex items-center justify-between w-full p-3 md:p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700/50 transition-colors"
        >
          <div className="flex items-center gap-2 md:gap-3">
            <LogOut className="w-4 h-4 md:w-5 md:h-5 text-neutral-600 dark:text-neutral-400" />
            <span className="text-sm md:text-base">{t('profile.actions.logout')}</span>
          </div>
          <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-neutral-400" />
        </button>
      </div>
    </Card>
  );

  return (
    <div className="max-h-[calc(100dvh)] w-full overflow-y-auto bg-neutral-100 dark:bg-neutral-900">
      {/* Hero Section - Use padding approach for mobile */}
      <div className="relative md:h-80 pt-16 pb-28 md:pt-0 md:pb-0 w-full">
        <div className="absolute inset-0 bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900" />
        <div className="absolute inset-0 opacity-5 bg-[radial-gradient(circle_at_center,_white_1px,_transparent_1px)] bg-[length:24px_24px]" />
        
        {/* Profile Info - Better positioning for mobile */}
        <div className="relative md:absolute md:bottom-4 md:left-0 md:right-0">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center md:items-center gap-4 md:gap-6">
              <Avatar className="w-24 h-24 md:w-40 md:h-40 border-4 border-white dark:border-neutral-800 shadow-xl">
                <AvatarImage src={user.avatar} />
                <AvatarFallback className="text-2xl md:text-4xl">{getInitials(user.username)}</AvatarFallback>
              </Avatar>
              
              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-2">
                  <h1 className="text-xl md:text-3xl font-bold text-white">{user.first_name + " "+ user.last_name}</h1>
                  <BadgeCheck className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <p className="text-sm md:text-base text-neutral-200 mt-1">{user.role_name}</p>
              </div>

              {/* Stat boxes in a row on mobile */}
              <div className="flex flex-row md:items-center gap-4 mt-3 md:mt-0">
                <div className="text-center px-4 md:px-6 py-2 md:py-3 bg-white/10 backdrop-blur rounded-lg">
                  <p className="text-xl md:text-2xl font-bold text-white">{stats.recommendationsCount}</p>
                  <p className="text-xs md:text-sm text-neutral-200">{t('profile.stats.places')}</p>
                </div>
              <div className="text-center px-4 md:px-6 py-2 md:py-3 bg-white/10 backdrop-blur rounded-lg">
                <p className="text-xl md:text-2xl font-bold text-white">{stats.likesCount}</p>
                <p className="text-xs md:text-sm text-neutral-200">
                  {stats.likesCount === 1 
                    ? t('profile.stats.like') 
                    : t('profile.stats.likes')}
                </p>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Adjusted padding for mobile */}
      <div className="max-w-7xl mx-auto px-4 py-4 md:py-8 pb-24 md:pb-8">
        {/* Updated grid with custom column widths for better proportions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8">
          {/* Left Column - Made narrower (1/3 width) */}
          <div className="lg:col-span-1 space-y-4 lg:space-y-6">
            {/* Balance and Quick Stats - Stacked in mobile, side by side in desktop */}
            <div className="space-y-4 lg:space-y-6">
              {/* Points Card - Full width on mobile */}
              <Card className="w-full overflow-hidden">
                <div className="relative p-4 md:p-6">
                  <div className="absolute inset-0 bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900" />
                  <div className="absolute inset-0 opacity-5 bg-[radial-gradient(circle_at_center,_white_1px,_transparent_1px)] bg-[length:24px_24px]" />
                  
                  <div className="relative z-10 flex justify-between items-start">
                    <div>
                      <p className="text-white/80 text-xs md:text-sm font-medium">{t('profile.balance.total')}</p>
                      <h2 className="text-3xl md:text-4xl font-bold text-white mt-0.5 md:mt-1">{stats.points}</h2>
                      <p className="text-white/80 text-xs md:text-sm mt-0.5 md:mt-1">{t('profile.balance.available')}</p>
                    </div>
                    <div className="p-2 md:p-3 bg-white/10 rounded-lg backdrop-blur-sm">
                      <CreditCard className="w-6 h-6 md:w-8 md:h-8 text-white" />
                    </div>
                  </div>
                </div>
                <div className="p-3 md:p-4 bg-neutral-50 dark:bg-neutral-800/50">
                  <div className="flex justify-between items-center">
                    <p className="text-xs md:text-sm text-neutral-600 dark:text-neutral-400">{t('profile.balance.quickActions')}</p>
                    <button className="text-xs md:text-sm text-primary hover:text-primary/80">
                      {t('profile.balance.viewHistory')} →
                    </button>
                  </div>
                </div>
              </Card>

              {/* Quick Stats - Full width on mobile */}
              <Card className="w-full p-4 md:p-6">
                <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4">{t('profile.quickStats.title')}</h3>
                <div className="space-y-3 md:space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className="p-1.5 md:p-2 rounded-lg bg-primary/10">
                        <MapPin className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                      </div>
                      <span className="text-sm md:text-base">{t('profile.quickStats.parkings')}</span>
                    </div>
                    <span className="text-sm md:text-base font-semibold">{stats.activeSpots || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className="p-1.5 md:p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                        <Building className="w-4 h-4 md:w-5 md:h-5 text-green-600 dark:text-green-400" />
                      </div>
                      <span className="text-sm md:text-base">{t('profile.quickStats.totalEarnings')}</span>
                    </div>
                    <span className="text-sm md:text-base font-semibold">
                      {combinedActivities
                        .filter(item => item.amount)
                        .reduce((sum, item) => sum + item.amount, 0)} pts
                    </span>
                  </div>
                </div>
              </Card>

              {/* Profile Actions on desktop - Right below Quick Stats */}
              {!isMobileView && <ProfileActionsCard />}
            </div>
          </div>

          {/* Right Column - Activity Table */}
          <div className="lg:col-span-2 space-y-4 md:space-y-6 mt-4 lg:mt-0">
            {/* Filters Card with Title */}
            <Card className="p-4 bg-white dark:bg-neutral-900">
              {/* Title Section */}
              <div className="flex justify-between items-center mb-3 md:mb-4">
                <div>
                  <h2 className="text-base md:text-lg font-semibold">{t('profile.activity.title')}</h2>
                  <p className="text-xs text-muted-foreground">{t('profile.activity.subtitle')}</p>
                </div>
              </div>

              {/* Search and Filters - Stack on mobile */}
              <div className="flex flex-col sm:flex-row justify-between gap-3 md:gap-4">
                {/* Search */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
                  <input
                    type="text"
                    placeholder={t('profile.activity.searchPlaceholder')}
                    className="pl-10 pr-4 py-2 w-full rounded-lg bg-neutral-100 dark:bg-neutral-800 border border-transparent focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-sm"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                
                {/* Type Filter - Full width on mobile */}
                <div className="w-full sm:w-48">
                  <Select
                    value={typeFilter}
                    onValueChange={handleTypeFilterChange}
                  >
                    <SelectTrigger className="bg-neutral-100 dark:bg-neutral-800 w-full">
                      <SelectValue placeholder={t('profile.filters.allActivities')} />
                    </SelectTrigger>
                                         <SelectContent>
                       <SelectItem value="all">{t('profile.filters.allActivities')}</SelectItem>
                       <SelectItem value="activity">{t('profile.filters.generalActivity')}</SelectItem>
                       <SelectItem value="parking">{t('profile.filters.parkingEarnings')}</SelectItem>
                       <SelectItem value="recommendation_add">{t('profile.filters.addRecommendation')}</SelectItem>
                       <SelectItem value="recommendation_like">{t('profile.filters.likeRecommendation')}</SelectItem>
                       <SelectItem value="daily_parking_reward">{t('profile.filters.dailyParkingReward')}</SelectItem>
                     </SelectContent>
                  </Select>
                </div>
              </div>
            </Card>

            {/* Activity table for desktop, cards for mobile */}
            {isMobileView ? (
              // Mobile Activity Cards
              <div className="space-y-1">
                {currentItems.length > 0 ? (
                  currentItems.map(item => (
                    <ActivityCard key={`mobile-${item.source}-${item.id}`} item={item} />
                  ))
                ) : (
                  <Card className="p-8 text-center">
                    <p className="text-muted-foreground">{t('profile.activity.noActivities')}</p>
                  </Card>
                )}
              </div>
            ) : (
              // Desktop Activity Table
              <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-neutral-50 dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{t('profile.table.description')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{t('profile.table.type')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{t('profile.table.date')}</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{t('profile.table.points')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                      {currentItems.length > 0 ? (
                        currentItems.map(item => (
                          <tr key={`${item.source}-${item.id}`} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap">
                              {item.description}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium 
                                  ${item.source === 'activity' 
                                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' 
                                    : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                                  }`}>
                                  {item.source === 'activity' ? t('profile.activity.label') : t('profile.parking.label')}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {item.date.toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ml-auto
                                ${item.amount > 0 
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                }`}>
                                {item.amountText}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="px-6 py-12 text-center text-muted-foreground">
                            {t('profile.activity.noMatchingActivities')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
            
            {/* Pagination Controls - Simplified for mobile */}
            {filteredActivities.length > 0 && (
              <Card className="p-3 md:p-4">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                  <div className="text-xs md:text-sm text-neutral-500 dark:text-neutral-400 hidden sm:block">
                    {t('profile.pagination.showing', {
                      from: indexOfFirstItem + 1,
                      to: Math.min(indexOfLastItem, filteredActivities.length),
                      total: filteredActivities.length
                    })}
                  </div>
                  
                  <div className="flex items-center space-x-1 md:space-x-2">
                    <button 
                      onClick={prevPage} 
                      disabled={currentPage === 1}
                      className={`p-1 rounded-md ${currentPage === 1 
                        ? 'text-neutral-300 dark:text-neutral-600 cursor-not-allowed' 
                        : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'}`}
                      aria-label={t('profile.pagination.previous')}
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    
                    {pageNumbers.map(number => (
                      <button
                        key={number}
                        onClick={() => goToPage(number)}
                        className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm ${
                          currentPage === number 
                            ? 'bg-primary text-white' 
                            : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                        }`}
                        aria-label={t('profile.pagination.pageNumber', { number })}
                      >
                        {number}
                      </button>
                    ))}
                    
                    <button 
                      onClick={nextPage} 
                      disabled={currentPage === totalPages}
                      className={`p-1 rounded-md ${currentPage === totalPages 
                        ? 'text-neutral-300 dark:text-neutral-600 cursor-not-allowed' 
                        : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'}`}
                      aria-label={t('profile.pagination.next')}
                    >
                      <ChevronRightIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </Card>
            )}

            {/* Profile Actions - Only on mobile, below the Activity Card */}
            {isMobileView && <ProfileActionsCard />}
          </div>
        </div>
      </div>
    </div>      
  );
};

export default Profile;